import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { toast } from "sonner"
import { type ShareReportFormValues, type ShareReportModalProps, shareReportSchema } from "../../types/share-report"
import BusyButton from "@/components/raytd/busy-button"

export function ShareReportModal({
    isOpen,
    onClose,
    onSubmit,
    isAllowShareConfigurable = true,
    defaultAllowShare = false,
    isAllowExportConfigurable = true,
    defaultAllowExport = false,
}: ShareReportModalProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);

    console.debug('ShareReportModal', { isAllowShareConfigurable, defaultAllowShare })

    // Initialize form with react-hook-form and zod validation
    const defaultValues = {
        first_name: "",
        last_name: "",
        email: "",
        allow_share: defaultAllowShare,
        allow_export: defaultAllowExport,
    }

    const form = useForm<ShareReportFormValues>({
        resolver: zodResolver(shareReportSchema),
        defaultValues: defaultValues,
    })

    // Reset form when the modal opens
    useEffect(() => {
        if (isOpen) {
            // Reset form to ensure it's clear or uses updated defaults
            form.reset({
                ...defaultValues,
                allow_share: defaultAllowShare, // Reset with potentially updated defaultAllowShare
                allow_export: defaultAllowExport,
            })
        }
    }, [isOpen, defaultAllowShare, defaultAllowExport])

    // Handle form submission
    const handleSubmit = async (data: ShareReportFormValues) => {
        const submissionData = {
            ...data,
            allow_share: isAllowShareConfigurable ? data.allow_share : defaultAllowShare,
            allow_export: isAllowExportConfigurable ? data.allow_export : defaultAllowExport,
        }

        try {
            setIsSubmitting(true)
            await onSubmit(submissionData)

            // Show success message
            toast.success(`The report has been shared with ${submissionData.email}`)

            // Reset form and close modal
            form.reset()
            onClose()
        } catch (error) {
            // Show error message
            toast.error("Failed to share report");
            console.debug(error);

        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && !isSubmitting && onClose()}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle className="text-xl font-bold">Share Report</DialogTitle>
                    <DialogDescription className="text-base text-zinc-800">
                        Who would you like to share this report with?
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="first_name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>First Name</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Start typing..." className="border-zinc-300 rounded-md" {...field} disabled={isSubmitting} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="last_name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Last Name</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Start typing..." className="border-zinc-300 rounded-md" {...field} disabled={isSubmitting} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Email Address</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="email"
                                            placeholder="Start typing..."
                                            className="border-zinc-300 rounded-md"
                                            {...field}
                                            disabled={isSubmitting}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="allow_share"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-center justify-between">
                                    <FormLabel>
                                        Allow the recipient to share the report with other people?
                                    </FormLabel>
                                    <Select
                                        onValueChange={field.onChange}
                                        defaultValue={String(field.value)}
                                        disabled={!isAllowShareConfigurable || isSubmitting}
                                    >
                                        <FormControl>
                                            <SelectTrigger className="w-[100px]">
                                                <SelectValue placeholder="Select" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="true">Yes</SelectItem>
                                            <SelectItem value="false">No</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="allow_export"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-center justify-between">
                                    <FormLabel>
                                        Allow the recipient to export the report?
                                    </FormLabel>
                                    <Select
                                        onValueChange={field.onChange}
                                        value={String(field.value)}
                                        disabled={!isAllowExportConfigurable || isSubmitting}
                                    >
                                        <FormControl>
                                            <SelectTrigger className="w-[100px]">
                                                <SelectValue placeholder="Select" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="true">Yes</SelectItem>
                                            <SelectItem value="false">No</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </FormItem>
                            )}
                        />

                        <DialogFooter className="gap-2 sm:gap-0">
                            <Button type="button" variant="outline" onClick={onClose} className="rounded-md" disabled={isSubmitting}>
                                Cancel
                            </Button>
                            <BusyButton
                                type="submit"
                                disabled={isSubmitting}
                                className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-md"
                                isBusy={isSubmitting}
                                busyText="Sharing..."
                            >
                                Share
                            </BusyButton>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

